import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const GeoContext = createContext(null);
const STORAGE_KEY = "ivtf_geo";

export function GeoProvider({ children }) {
  const [geo, setGeo] = useState(null); // {lat, lng, city, state, source}
  const [status, setStatus] = useState("idle"); // idle | locating | granted | denied | ip

  useEffect(() => {
    // Try cached value first
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Date.now() - parsed.cached_at < 24 * 60 * 60 * 1000) {
          setGeo(parsed);
          setStatus(parsed.source === "browser" ? "granted" : "ip");
          return;
        }
      } catch {}
    }
    // Silent IP fallback
    api.get("/geo/ip").then(({ data }) => {
      if (data?.lat) {
        const entry = { ...data, cached_at: Date.now() };
        setGeo(entry);
        setStatus("ip");
        localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
      }
    }).catch(() => {});
  }, []);

  const requestPrecise = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus("denied");
      return Promise.resolve(null);
    }
    setStatus("locating");
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const entry = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            source: "browser",
            cached_at: Date.now(),
          };
          setGeo(entry);
          setStatus("granted");
          localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
          resolve(entry);
        },
        () => {
          setStatus("denied");
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60_000 }
      );
    });
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setGeo(null);
    setStatus("idle");
  }, []);

  return (
    <GeoContext.Provider value={{ geo, status, requestPrecise, clear }}>
      {children}
    </GeoContext.Provider>
  );
}

export const useGeo = () => useContext(GeoContext) || { geo: null, status: "idle", requestPrecise: () => {}, clear: () => {} };
